"use client";

import { createContext, useContext } from "react";
import type { ModerationProfile } from "shared/ModerationProfile";
import type { OrganisationSummary } from "shared/Organisation";
import type { PolicyPresetId } from "shared/PolicyPresetIds";
import type { PrivatePatchProjectBody } from "shared/PrivateApiSchemas";
import type { Project } from "shared/Project";
import type { ProjectTeam } from "shared/ProjectAccess";
import type { PortalShellData } from "src/lib/PortalShellData";

export interface PortalContextState {
  initialShellData?: PortalShellData;
  /** The signed-in user's organisations, personal first. */
  organisations: OrganisationSummary[];
  /** The organisation whose projects, policies and billing the portal shows. */
  selectedOrganisation: OrganisationSummary | null;
  selectedOrgId: string | null;
  projectTeam?: ProjectTeam;
  projectTeamError?: boolean;
  reloadProjectTeam?: () => Promise<void>;
  moderationProfiles: ModerationProfile[];
  moderationProfilesError: boolean;
  selectedProject: Project | null;
  selectedProjectId: string | null;
  projects: Project[];
  projectsHasMore: boolean;
  projectsPageIndex: number;
  projectsLoading: boolean;
}

export interface PortalActions {
  /**
   * Remembers `orgId` as the selected organisation and forgets the selected project, so the next
   * layout load (`useAppRouter().refresh()`) opens that organisation's first project.
   */
  selectOrganisation: (orgId: string) => void;
  /** Reloads the organisations, for their names and free upload counts. */
  reloadOrganisations: () => Promise<OrganisationSummary[]>;
  reloadModerationProfiles: () => Promise<ModerationProfile[]>;
  setSelectedProjectId: (projectId: string | null) => void;
  setProjects: (projects: Project[]) => void;
  loadMoreProjects: () => Promise<void>;
  /** Creates a project in the selected organisation, starting on a built-in policy (General by default). */
  createProject: (
    name: string,
    description?: string,
    policyPresetId?: PolicyPresetId,
  ) => Promise<Project>;
  updateProject: (projectId: string, updates: PrivatePatchProjectBody) => Promise<Project>;
}

export type PortalContextType = PortalContextState & PortalActions;

export const PortalContext = createContext<PortalContextType | undefined>(undefined);

export const usePortal = () => {
  const context = useContext(PortalContext);
  if (context === undefined) {
    throw new Error("usePortal must be used within a PortalProvider");
  }
  return context;
};
